import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { StudentsApi } from "@/openapi/generated";
import type { CreateStudentRequest } from "@/openapi/generated/models";
import { invalidateStudentRosterQueries } from "@/features/students/shared/invalidateStudentRosterQueries";
import {
  createAuthenticatedApi,
  EMAIL_ALREADY_IN_USE_MESSAGE,
  getApiErrorMessage,
  throwIfResultFailed,
} from "@/lib/utils";

export const useStudentCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (createStudentRequest: CreateStudentRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(StudentsApi, accessToken);
      const result = await service.apiStudentsPost({
        createStudentRequest,
      });

      throwIfResultFailed(result, "Failed to create student");
      return result;
    },
    onSuccess: async () => {
      await invalidateStudentRosterQueries(queryClient);
      toast({
        title: "Student created",
        description: "Student created successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to create student",
        { conflictFallback: EMAIL_ALREADY_IN_USE_MESSAGE },
      );

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};

